import {logError} from '@/utilities/log';
import {ChangeEvent} from 'preact/compat';
import {useEffect, useState} from 'preact/hooks';

type SellingPlan = {
  id: number;
  name: string;
  options: {value: string}[];
  price_adjustments: {value: number}[];
};

export type SellingPlanSelectorProps = {
  formId?: string;
  sellingPlans: SellingPlan[];
  defaultPlan?: SellingPlan | null;
  onUpdateSubscribeAndSave?: (value: boolean) => void;
  onUpdateSellingPlan?: (value: number) => void;
  showSavingsPercent?: boolean;
  checked?: boolean;
};

/**
 * Form control for selecting a selling plan.
 * If the user has not made a selection, the default plan is selected.
 * Once a selection is made, it is maintained, unless it is no longer available.
 * Users can also toggle between subscription and onetime purchase.
 *
 * @param sellingPlans - The selling plans available for the current variant
 * @param defaultPlan - The default selling plan to select
 * @param onUpdateSubscribeAndSave - Callback for when the user toggles between subscription and onetime purchase
 * @param onUpdateSellingPlan - Callback for when the user selects a selling plan
 * @param showSavingsPercent - Whether to show the savings percentage for the selected selling plan
 * @param checked - Whether the subscription is checked by default
 */
export default function SellingPlanSelector({
  formId,
  defaultPlan,
  sellingPlans,
  onUpdateSellingPlan,
  onUpdateSubscribeAndSave,
  showSavingsPercent = true,
  checked,
}: SellingPlanSelectorProps) {
  const [selected, setSelected] = useState<SellingPlan | null>(null);

  const [subscribeAndSave, setSubscribeAndSave] = useState(checked ?? true);

  useEffect(() => {
    if (!selected) return;

    // Update the selection if the selected selling plan is not available
    const isUnavailable = !sellingPlans.some((p) => p.id === selected?.id);

    if (isUnavailable) {
      setSelected(null);
    }
  }, [selected, sellingPlans]);

  const plan = selected ?? defaultPlan;

  if (!plan) return <div></div>;

  const savings = plan.price_adjustments[0]?.value;
  const [interval, unit] = plan.options[1].value.split('-');
  const unitPlural = interval === '1' ? unit : `${unit}s`;

  const handleCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSubscribeAndSave(e.currentTarget.checked);
    onUpdateSubscribeAndSave?.(!!e.currentTarget.checked);
  };

  const handleSelectChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const id = Number(e.currentTarget.value);
    const selectedPlan = sellingPlans.find((p) => p.id === id);

    if (!selectedPlan) {
      logError(
        `Selected selling plan with id ${id} not found in available selling plans`
      );
      return;
    }

    setSelected(selectedPlan);
    onUpdateSellingPlan?.(id);
  };

  return (
    <div className="group/plans fast-transition flex flex-col rounded-3xl border border-deep-ocean-blue/20 has-[#subscribe-and-save:checked]:border-2 has-[#subscribe-and-save:checked]:border-deep-ocean-blue has-[#subscribe-and-save:checked]:bg-cloud-white">
      <div className="flex items-center justify-start gap-x-2 p-3">
        <label className="fast-transition relative h-6 w-12 cursor-pointer rounded-full border border-deep-ocean-blue/20 p-[2px] has-[:checked]:border-2 has-[:checked]:border-deep-ocean-blue">
          <input
            type="checkbox"
            checked={subscribeAndSave}
            onChange={handleCheckboxChange}
            className="peer hidden"
            id="subscribe-and-save"
            name="subscribe-and-save"
            form={formId}
          />
          <div className="fast-transition absolute inset-y-1/2 left-0.5 size-4 -translate-y-1/2 cursor-pointer rounded-full bg-deep-ocean-blue peer-[:checked]:translate-x-6  peer-[:checked]:bg-breeze-blue"></div>
        </label>
        <div className="flex flex-col items-start leading-none">
          <strong>
            Subscribe & Save
            {savings && showSavingsPercent ? ` ${savings}%` : ''}
          </strong>
          <label
            htmlFor="show-selling-plans"
            className="fast-transition max-h-4 group-has-[#subscribe-and-save:not(:checked)]/plans:max-h-0 group-has-[#subscribe-and-save:not(:checked)]/plans:opacity-0"
          >
            <span className="text-xs font-bold text-breeze-blue">
              Renews every{' '}
              <a className="cursor-pointer">
                {interval} {unitPlural}
              </a>
            </span>
          </label>
        </div>
      </div>
      <input type="checkbox" className="peer hidden" id="show-selling-plans" />
      <div className="max-h-0 overflow-y-hidden border-t border-t-deep-ocean-blue opacity-0 transition-all duration-200 ease-in-out group-has-[#subscribe-and-save:checked]/plans:peer-[:checked]:max-h-12 group-has-[#subscribe-and-save:checked]/plans:peer-[:checked]:opacity-100">
        <select
          id="selling-plan"
          name="selling-plan"
          className="w-full rounded-b-3xl border-none p-3 pr-12 outline-none focus:outline-none"
          form={formId}
          value={plan.id}
          onChange={handleSelectChange}
        >
          {sellingPlans.map((plan) => (
            <option key={plan.id} value={plan.id}>
              {plan.name}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
